import { ListItemButton, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { DATA_TEST, getDynamicDataTestId } from '../../enums';

import { MuiSxProps } from '../../types';
import { useIsContentOverflowing } from '../../utils/hooks/useIsContentOverflowing';
import { IEntityAutocompleteOption } from './DesktopEntityAutoCompleteSearch';
import { IEntityOptionMuiProps } from './ReactWindowListBoxComponent';

interface IEntitySwitcherAutoCompleteRenderOptionProps extends IEntityOptionMuiProps {
  entity: IEntityAutocompleteOption;
  displayEntityNamesOnly?: boolean;
  styleOverrides?: MuiSxProps;
  onItemClick(entity: IEntityAutocompleteOption): void;
}

export const EntitySwitcherAutoCompleteRenderOption: React.FC<IEntitySwitcherAutoCompleteRenderOptionProps> = ({
  entity,
  displayEntityNamesOnly,
  onClick,
  onItemClick,
  styleOverrides: renderStyles,
  ...muiListItemProps
}) => {
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const subdomainRef = React.useRef<HTMLSpanElement>(null);
  const entityTypeRef = React.useRef<HTMLSpanElement>(null);
  const labelIsOverflowing = useIsContentOverflowing(labelRef);
  const subdomainIsOverflowing = useIsContentOverflowing(subdomainRef);
  const entityTypeIsOverflowing = useIsContentOverflowing(entityTypeRef);

  return (
    <ListItemButton
      {...muiListItemProps}
      sx={{
        '&.MuiAutocomplete-option': {
          px: 2
        },
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        cursor: 'pointer',
        '&.Mui-focusVisible, &:focus-visible': {
          outline: (theme: any) => `2px solid ${theme.palette.primary.main}`
        },
        ...renderStyles
      }}
      aria-labelledby="overflow-entity-menu-list"
      onClick={(event: any) => {
        // this onClick is used to close the menu when an item is clicked (from MUI)
        onClick?.(event);
        // this onClick is used to set the value
        onItemClick(entity);
      }}
      data-test={getDynamicDataTestId(DATA_TEST.entity_autocomplete_list_item, entity.subdomain)}
    >
      <Tooltip
        title={labelIsOverflowing ? entity.label : ''}
        placement="top"
        enterNextDelay={500}
        disableInteractive={true}
      >
        <Typography
          component="span"
          variant="h4"
          ref={labelRef}
          sx={{
            pr: 2,
            flex: '3 0',
            color: 'text.primary'
          }}
          noWrap={true}
        >
          {entity.label}
        </Typography>
      </Tooltip>
      {!displayEntityNamesOnly && (
        <>
          <Tooltip
            title={entityTypeIsOverflowing ? entity.entityType : ''}
            placement="top"
            enterNextDelay={500}
            disableInteractive={true}
          >
            <Typography
              ref={entityTypeRef}
              component="span"
              variant="bodyDefault"
              sx={{
                px: 2,
                flex: '1 0',
                textAlign: entity.entityType ? 'left' : 'center'
              }}
              noWrap={true}
            >
              {entity.entityType ? `${entity.entityType}` : '\u2013'}
            </Typography>
          </Tooltip>
          <Tooltip
            title={subdomainIsOverflowing ? entity.label : ''}
            placement="top"
            enterNextDelay={500}
            disableInteractive={true}
          >
            <Typography
              component="span"
              variant="bodyDefault"
              ref={subdomainRef}
              sx={{
                flex: '2 0',
                px: 2
              }}
              noWrap={true}
            >
              {entity.subdomain}
            </Typography>
          </Tooltip>
        </>
      )}
    </ListItemButton>
  );
};
